export interface CommandResult {
  success: boolean;
  message: string;
  data?: Record<string, unknown>;
}

export interface SlashCommand {
  command: string;
  description: string;
  handler: (args: string[]) => Promise<CommandResult> | CommandResult;
  requiresApproval?: boolean;
  category: 'system' | 'agent' | 'development' | 'deployment';
}

class CommandRouter {
  private commands: Map<string, SlashCommand> = new Map();

  constructor() {
    this.registerDefaultCommands();
  }

  private registerDefaultCommands() {
    this.register({
      command: '/status',
      description: 'Get system and agent status information',
      category: 'system',
      handler: this.handleStatus
    });

    this.register({
      command: '/manual',
      description: 'Switch to manual control mode',
      category: 'system',
      handler: this.handleManualMode
    });

    this.register({
      command: '/semi',
      description: 'Switch to semi-autonomous mode',
      category: 'system',
      handler: this.handleSemiMode
    });

    this.register({
      command: '/full',
      description: 'Switch to full autonomous mode',
      category: 'system',
      handler: this.handleFullMode
    });

    this.register({
      command: '/rebase',
      description: 'Rebase current branch with latest changes',
      category: 'development',
      requiresApproval: true,
      handler: this.handleRebase
    });

    this.register({
      command: '/fix',
      description: 'Auto-fix code issues and linting errors',
      category: 'development',
      handler: this.handleFix
    });

    this.register({
      command: '/deploy',
      description: 'Deploy application to specified environment',
      category: 'deployment',
      requiresApproval: true,
      handler: this.handleDeploy
    });

    this.register({
      command: '/retry',
      description: 'Retry a specific task by ID',
      category: 'agent',
      handler: this.handleRetry
    });

    this.register({
      command: '/recall',
      description: 'Recall output from a specific task',
      category: 'agent',
      handler: this.handleRecall
    });

    this.register({
      command: '/exit',
      description: 'End session and generate summary',
      category: 'system',
      handler: this.handleExit
    });

    this.register({
      command: '/help',
      description: 'Show available commands',
      category: 'system',
      handler: this.handleHelp
    });

    this.register({
      command: '/terminal',
      description: 'Toggle terminal panel visibility',
      category: 'development',
      handler: this.handleTerminal
    });

    this.register({
      command: '/simulate',
      description: 'Simulate webhook and CI events',
      category: 'development',
      handler: this.handleSimulate
    });
  }

  register(command: SlashCommand) {
    this.commands.set(command.command, command);
  }

  async execute(input: string): Promise<CommandResult | null> {
    const trimmed = input.trim();
    if (!trimmed.startsWith('/')) {
      return null;
    }

    const parts = trimmed.split(' ');
    const commandName = parts[0];
    const args = parts.slice(1);

    const command = this.commands.get(commandName);
    if (!command) {
      return {
        success: false,
        message: `Unknown command: ${commandName}. Type /help for available commands.`
      };
    }

    try {
      return await command.handler(args);
    } catch (error) {
      return {
        success: false,
        message: `Error executing ${commandName}: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  getCommands(): SlashCommand[] {
    return Array.from(this.commands.values());
  }

  getCommandsByCategory(category: SlashCommand['category']): SlashCommand[] {
    return this.getCommands().filter(cmd => cmd.category === category);
  }

  private handleStatus = async (_args: string[]): Promise<CommandResult> => {
    const status = {
      agents: {
        'agent-001': { status: 'active', task: 'Analyzing market data', confidence: 85 },
        'agent-002': { status: 'idle', task: 'Waiting for tasks', confidence: 92 },
        'agent-003': { status: 'error', task: 'Failed to process PDF', confidence: 45 }
      },
      system: {
        uptime: '2h 15m',
        memory: '23.4MB',
        activeConnections: 12
      },
      lastActivity: new Date().toISOString()
    };

    return {
      success: true,
      message: 'System status retrieved successfully',
      data: status
    };
  };

  private handleManualMode = async (_args: string[]): Promise<CommandResult> => {
    return {
      success: true,
      message: 'Switched to manual control mode. All actions require explicit approval.'
    };
  };

  private handleSemiMode = async (_args: string[]): Promise<CommandResult> => {
    return {
      success: true,
      message: 'Switched to semi-autonomous mode. Safe operations will execute automatically.'
    };
  };

  private handleFullMode = async (_args: string[]): Promise<CommandResult> => {
    return {
      success: true,
      message: 'Switched to full autonomous mode. Maximum AI control enabled.'
    };
  };

  private handleRebase = async (args: string[]): Promise<CommandResult> => {
    const targetBranch = args[0] || 'main';
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    return {
      success: true,
      message: `Successfully rebased current branch with ${targetBranch}`,
      data: {
        branch: targetBranch,
        conflicts: 0,
        commits: 3
      }
    };
  };

  private handleFix = async (_args: string[]): Promise<CommandResult> => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const fixes = [
      'Fixed 3 ESLint errors',
      'Resolved 2 TypeScript warnings',
      'Formatted 5 files with Prettier'
    ];

    return {
      success: true,
      message: 'Auto-fix completed successfully',
      data: {
        fixes,
        filesModified: 5
      }
    };
  };

  private handleDeploy = async (args: string[]): Promise<CommandResult> => {
    const env = args[0] || 'staging';
    
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    return {
      success: true,
      message: `Deployment to ${env} completed successfully`,
      data: {
        environment: env,
        deploymentId: `deploy_${Date.now()}`,
        url: `https://${env}.example.com`
      }
    };
  };

  private handleRetry = async (args: string[]): Promise<CommandResult> => {
    const taskId = args[0];
    if (!taskId) {
      return {
        success: false,
        message: 'Please specify a task ID to retry. Usage: /retry <taskId>'
      };
    }

    await new Promise(resolve => setTimeout(resolve, 2000));

    const retryResults: Record<string, string> = {
      'task_003': 'Retry successful: Test suite executed with DATABASE_URL configured. All 47 tests passed.',
      'task_004': 'Retry failed: Security scan still encountering network timeout. Please check firewall settings.',
      'task_005': 'Retry successful: Documentation generation completed after resolving TypeScript compilation errors.',
      'task_001': 'Retry successful: Code analysis re-run with updated configuration. Found 1 additional optimization opportunity.',
      'task_002': 'Retry successful: Deployment completed to staging environment with health checks passing.'
    };

    const retryResult = retryResults[taskId] || 'Retry successful: Task completed with updated configuration.';

    return {
      success: !retryResult.includes('failed'),
      message: retryResult,
      data: {
        taskId,
        status: retryResult.includes('successful') ? 'completed' : 'failed',
        operation: 'retry',
        timestamp: new Date().toISOString(),
        retryCount: Math.floor(Math.random() * 3) + 1
      }
    };
  };

  private handleRecall = async (args: string[]): Promise<CommandResult> => {
    const taskId = args[0];
    if (!taskId) {
      return {
        success: false,
        message: 'Please specify a task ID to recall. Usage: /recall <taskId>'
      };
    }

    await new Promise(resolve => setTimeout(resolve, 1500));

    const mockTaskOutputs: Record<string, string> = {
      'task_001': 'Code analysis completed: Found 3 performance bottlenecks in rendering pipeline. Recommended implementing virtual scrolling for large datasets.',
      'task_002': 'Deployment successful: Application deployed to staging environment at https://staging.example.com',
      'task_003': 'Test execution failed: Missing environment variable DATABASE_URL. Please configure before retrying.',
      'task_004': 'Security scan completed: No critical vulnerabilities found. 2 minor issues require attention.',
      'task_005': 'Documentation generated: API docs created with 98% coverage at /docs/api'
    };

    const output = mockTaskOutputs[taskId] || `Task ${taskId} output: Successfully completed agent operation with detailed results and recommendations.`;

    return {
      success: true,
      message: `Recalled task ${taskId}`,
      data: {
        taskId,
        output,
        operation: 'recall',
        timestamp: new Date().toISOString()
      }
    };
  };

  private handleExit = async (_args: string[]): Promise<CommandResult> => {
    await new Promise(resolve => setTimeout(resolve, 2000));

    const sessionSummary = {
      summary: 'Successfully completed Beehive ChatShell development with comprehensive agent memory integration and ARCOS webhook routing.',
      duration: 2700,
      totalBC: 15420,
      tasksCompleted: 3,
      keyAchievements: [
        'Successfully implemented Beehive ChatShell Phase 10-15 features',
        'Integrated agent memory panel with task history and recall',
        'Established ARCOS webhook routing for real-time updates',
        'Created comprehensive plugin card rendering system'
      ],
      recommendations: [
        'Monitor agent memory performance and optimize recall queries',
        'Expand webhook integration for additional CI/CD events',
        'Implement advanced memory search and filtering capabilities',
        'Consider adding collaborative session features'
      ]
    };

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('beehive:session-end', {
        detail: { summary: sessionSummary, timestamp: new Date() }
      }));
    }

    return {
      success: true,
      message: 'Generating session summary and ending session...',
      data: {
        summary: sessionSummary,
        sessionEnded: true,
        timestamp: new Date().toISOString(),
        triggerSummary: true
      }
    };
  };

  private handleHelp = async (_args: string[]): Promise<CommandResult> => {
    const commandsByCategory = {
      system: this.getCommandsByCategory('system'),
      agent: this.getCommandsByCategory('agent'),
      development: this.getCommandsByCategory('development'),
      deployment: this.getCommandsByCategory('deployment')
    };

    const helpText = Object.entries(commandsByCategory)
      .map(([category, commands]) => {
        const categoryHeader = `**${category.toUpperCase()} COMMANDS**`;
        const commandList = commands
          .map(cmd => `${cmd.command} - ${cmd.description}`)
          .join('\n');
        return `${categoryHeader}\n${commandList}`;
      })
      .join('\n\n');

    return {
      success: true,
      message: `Available Commands:\n\n${helpText}`,
      data: { commandsByCategory }
    };
  };

  private handleSimulate = async (args: string[]): Promise<CommandResult> => {
    const eventType = args[0] || 'all';
    
    if (typeof window !== 'undefined') {
      if (eventType === 'webhook' || eventType === 'all') {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('beehive:webhook', {
            detail: {
              type: 'pr_created',
              data: { number: 42, title: 'feat: implement new feature' }
            }
          }));
        }, 1000);
      }

      if (eventType === 'ci' || eventType === 'all') {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('beehive:ci', {
            detail: {
              status: 'success',
              workflow: 'CI/CD Pipeline',
              duration: '2m 34s'
            }
          }));
        }, 3000);
      }
    }

    return {
      success: true,
      message: `Simulating ${eventType} events. Check chat for incoming webhook results.`,
      data: { eventType, scheduled: true }
    };
  };

  private handleTerminal = async (_args: string[]): Promise<CommandResult> => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('beehive:terminal-toggle'));
    }

    return {
      success: true,
      message: 'Terminal panel toggled',
      data: { action: 'terminal-toggle' }
    };
  };
}

export const commandRouter = new CommandRouter();
